import { Component, computed, effect, inject, input, output, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { RouterLink } from '@angular/router';
import { ButtonModule } from 'primeng/button';
import { CheckboxModule } from 'primeng/checkbox';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { DialogModule } from 'primeng/dialog';
import { InputTextModule } from 'primeng/inputtext';
import { SelectModule } from 'primeng/select';
import { TableModule } from 'primeng/table';
import { TagModule } from 'primeng/tag';
import { ConfirmationService, MessageService } from 'primeng/api';
import { finalize, forkJoin } from 'rxjs';
import { CurrentContextService } from '../../core/context/current-context.service';
import { LocalizationService } from '../../core/localization/localization.service';
import { AinFormFieldComponent } from '../../shared/design-system/form-field.component';
import { InfoHintComponent } from '../../shared/design-system/info-hint.component';
import { AinStateComponent } from '../../shared/design-system/state.component';
import { DynamicApiService } from './dynamic-api.service';
import {
  AddRelationshipRequest,
  DYNAMIC_RELATIONSHIP_TYPE_CATALOG,
  DynamicEntity,
  DynamicEntityDetail,
  DynamicField,
  DynamicRelationship,
  DynamicRelationshipType
} from './dynamic.models';

interface RelationshipFormState {
  sourceEntityId: string;
  targetEntityId: string;
  relationshipType: DynamicRelationshipType;
  foreignKeyFieldName: string;
  displayName: string;
  isRequired: boolean;
  cascadeDelete: boolean;
}

type SelectOption<T = string> = { label: string; value: T };

@Component({
  standalone: true,
  selector: 'ain-dynamic-relationship-editor',
  imports: [
    AinStateComponent,
    AinFormFieldComponent,
    InfoHintComponent,
    TableModule,
    ButtonModule,
    DialogModule,
    InputTextModule,
    SelectModule,
    CheckboxModule,
    TagModule,
    ConfirmDialogModule,
    FormsModule,
    RouterLink
  ],
  providers: [MessageService, ConfirmationService],
  template: `
    <section class="relationship-editor">
      <div class="section-header">
        <h3>{{ t('dynamic.relationship.title') }}</h3>
        <ain-info-hint
          [titleKey]="'dynamic.relationship.info.title'"
          [descriptionKey]="'dynamic.relationship.info.description'"
          [bulletKeys]="[
            'dynamic.relationship.info.bullets.cardinality',
            'dynamic.relationship.info.bullets.cascade',
            'dynamic.relationship.info.bullets.migration'
          ]" />
      </div>

      @if (!canViewRelationships()) {
        <ain-state type="empty" [title]="t('dynamic.relationship.title')" [message]="t('common.noPermission')" />
      } @else if (loading()) {
        <ain-state type="loading" [title]="t('common.loading')" />
      } @else if (error()) {
        <ain-state type="error" [title]="t('common.loadFailed')" [message]="error() ?? ''" />
      } @else {
        @if (pendingMigrationNotice(); as migrationId) {
          <div class="notice card migration-notice" role="status">
            <span>{{ t('dynamic.relationship.pendingMigrationMessage') }}</span>
            <a [routerLink]="['/dynamic/migrations', migrationId]" pButton class="p-button-sm">{{ t('dynamic.relationship.reviewMigration') }}</a>
          </div>
        }

        @if (!relationships().length) {
          <ain-state type="empty" [title]="t('common.noRecords')" />
          @if (canCreateRelationship()) {
            <div class="empty-actions">
              <button pButton type="button" [label]="t('dynamic.relationship.add')" (click)="openAddDialog()"></button>
            </div>
          }
        } @else {
          <div class="toolbar">
            @if (canCreateRelationship()) {
              <button pButton type="button" [label]="t('dynamic.relationship.add')" (click)="openAddDialog()"></button>
            }
          </div>

          <p-table [value]="relationships()" dataKey="id">
            <ng-template pTemplate="header">
              <tr>
                <th>{{ t('dynamic.relationship.sourceEntity') }}</th>
                <th>{{ t('dynamic.relationship.targetEntity') }}</th>
                <th>{{ t('dynamic.relationship.type') }}</th>
                <th>{{ t('dynamic.relationship.fkColumnOrJoin') }}</th>
                <th>{{ t('dynamic.relationship.isRequired') }}</th>
                <th>{{ t('dynamic.relationship.cascadeBehavior') }}</th>
                <th>{{ t('columns.status') }}</th>
                <th style="width: 10rem"></th>
              </tr>
            </ng-template>
            <ng-template pTemplate="body" let-rel>
              <tr>
                <td>{{ entityLabel(rel.sourceEntityId) }}</td>
                <td>{{ entityLabel(rel.targetEntityId) }}</td>
                <td>{{ relationshipTypeLabel(rel.relationshipType) }}</td>
                <td>{{ fkOrJoinLabel(rel) }}</td>
                <td>{{ rel.isRequired ? t('common.yes') : t('common.no') }}</td>
                <td>{{ rel.cascadeDelete ? t('dynamic.relationship.cascadeDelete') : t('dynamic.relationship.restrictDelete') }}</td>
                <td>
                  <p-tag
                    [value]="rel.isActive ? t('dynamic.relationship.status.active') : t('dynamic.relationship.status.inactive')"
                    [severity]="rel.isActive ? 'success' : 'secondary'" />
                  @if (rel.pendingMigrationId) {
                    <a class="migration-link" [routerLink]="['/dynamic/migrations', rel.pendingMigrationId]">{{ t('dynamic.relationship.pendingMigration') }}</a>
                  }
                </td>
                <td class="row-actions">
                  @if (canUpdateRelationship()) {
                    <button pButton type="button" text [label]="t('dynamic.relationship.edit')" (click)="openEditDialog(rel)"></button>
                  }
                  @if (canDeleteRelationship()) {
                    <button pButton type="button" text severity="danger" [label]="t('dynamic.relationship.delete')" (click)="confirmDelete(rel)"></button>
                  }
                </td>
              </tr>
            </ng-template>
          </p-table>
        }
      }

      <p-dialog
        [header]="dialogMode() === 'add' ? t('dynamic.relationship.add') : t('dynamic.relationship.edit')"
        [(visible)]="relationshipDialogVisible"
        [modal]="true"
        [style]="{ width: 'min(640px, 94vw)' }"
        (onHide)="resetRelationshipForm()">
        @if (dialogMode() === 'add') {
          <div class="dialog-form">
            <ain-form-field [label]="t('dynamic.relationship.sourceEntity')">
              <p-select
                [options]="entityOptions()"
                optionLabel="label"
                optionValue="value"
                [(ngModel)]="relationshipForm.sourceEntityId"
                (ngModelChange)="onRelationshipEndpointsChange()"
                [filter]="true"
                appendTo="body" />
            </ain-form-field>

            <ain-form-field [label]="t('dynamic.relationship.targetEntity')">
              <p-select
                [options]="entityOptions()"
                optionLabel="label"
                optionValue="value"
                [(ngModel)]="relationshipForm.targetEntityId"
                (ngModelChange)="onRelationshipEndpointsChange()"
                [filter]="true"
                appendTo="body" />
            </ain-form-field>

            <ain-form-field [label]="t('dynamic.relationship.type')">
              <p-select
                [options]="relationshipTypeOptions()"
                optionLabel="label"
                optionValue="value"
                [(ngModel)]="relationshipForm.relationshipType"
                (ngModelChange)="onRelationshipTypeChange($event)"
                appendTo="body" />
            </ain-form-field>

            @if (showForeignKeyPicker()) {
              <ain-form-field
                [label]="t('dynamic.relationship.foreignKey')"
                [hint]="fkConventionHint()">
                <p-select
                  [options]="fkFieldOptions()"
                  optionLabel="label"
                  optionValue="value"
                  [(ngModel)]="relationshipForm.foreignKeyFieldName"
                  [filter]="true"
                  appendTo="body" />
              </ain-form-field>
            } @else {
              <div class="notice card">
                <p>{{ t('dynamic.relationship.manyToManyJoinHint') }}</p>
              </div>
            }

            <ain-form-field [label]="t('dynamic.relationship.displayName')">
              <input pInputText [(ngModel)]="relationshipForm.displayName" />
            </ain-form-field>

            <div class="checkbox-row">
              <label>
                <p-checkbox binary="true" [(ngModel)]="relationshipForm.isRequired" />
                <span>{{ t('dynamic.relationship.isRequired') }}</span>
              </label>
            </div>

            <ain-form-field [label]="t('dynamic.relationship.cascadeBehavior')">
              <p-select
                [options]="cascadeOptions()"
                optionLabel="label"
                optionValue="value"
                [(ngModel)]="relationshipForm.cascadeDelete"
                appendTo="body" />
            </ain-form-field>
          </div>
        } @else {
          <div class="dialog-form">
            <div class="checkbox-row">
              <label>
                <p-checkbox binary="true" [(ngModel)]="relationshipForm.isRequired" />
                <span>{{ t('dynamic.relationship.isRequired') }}</span>
              </label>
            </div>

            <ain-form-field [label]="t('dynamic.relationship.cascadeBehavior')">
              <p-select
                [options]="cascadeOptions()"
                optionLabel="label"
                optionValue="value"
                [(ngModel)]="relationshipForm.cascadeDelete"
                appendTo="body" />
            </ain-form-field>
          </div>
        }

        <ng-template pTemplate="footer">
          <button pButton type="button" severity="secondary" [label]="t('common.cancel')" (click)="relationshipDialogVisible = false"></button>
          <button pButton type="button" [label]="t('common.save')" [disabled]="savingRelationship()" (click)="saveRelationship()"></button>
        </ng-template>
      </p-dialog>

      <p-confirmDialog />
    </section>
  `,
  styles: [`
    .relationship-editor { display: grid; gap: 12px; }
    .section-header { display: grid; gap: 8px; }
    .section-header h3 { margin: 0; }
    .toolbar, .empty-actions { display: flex; justify-content: flex-end; }
    .row-actions { display: flex; gap: 4px; flex-wrap: wrap; }
    .dialog-form { display: grid; gap: 12px; }
    .checkbox-row label { display: inline-flex; align-items: center; gap: 8px; }
    .notice.card { border: 1px solid #e5e7eb; border-radius: 8px; padding: 10px 12px; background: #f9fafb; }
    .migration-notice { display: flex; align-items: center; justify-content: space-between; gap: 12px; flex-wrap: wrap; border-color: #bfdbfe; background: #eff6ff; }
    .migration-link { display: inline-block; margin-inline-start: 8px; font-size: 0.85rem; color: var(--ain-color-primary); }
  `]
})
export class DynamicRelationshipEditorComponent {
  private readonly api = inject(DynamicApiService);
  private readonly localization = inject(LocalizationService);
  private readonly messages = inject(MessageService);
  private readonly confirmation = inject(ConfirmationService);
  private readonly contextService = inject(CurrentContextService);

  readonly entity = input.required<DynamicEntityDetail>();
  readonly refresh = output<void>();

  readonly relationships = signal<DynamicRelationship[]>([]);
  readonly entities = signal<DynamicEntity[]>([]);
  readonly fkHolderFields = signal<DynamicField[]>([]);
  readonly loading = signal(true);
  readonly error = signal<string | null>(null);
  readonly savingRelationship = signal(false);
  readonly dialogMode = signal<'add' | 'edit'>('add');
  readonly pendingMigrationNotice = signal<string | null>(null);

  relationshipDialogVisible = false;
  relationshipForm: RelationshipFormState = this.emptyRelationshipForm();
  private editingRelationshipId: string | null = null;

  private readonly entityId = computed(() => this.entity().id);

  readonly entityOptions = computed<SelectOption[]>(() =>
    this.entities().map(item => ({
      label: `${item.displayName} (${item.name})`,
      value: item.id
    }))
  );

  readonly relationshipTypeOptions = computed<SelectOption<DynamicRelationshipType>[]>(() =>
    DYNAMIC_RELATIONSHIP_TYPE_CATALOG.map(item => ({
      label: this.t(item.labelKey),
      value: item.value
    }))
  );

  readonly cascadeOptions = computed<SelectOption<boolean>[]>(() => [
    { label: this.t('dynamic.relationship.cascadeDelete'), value: true },
    { label: this.t('dynamic.relationship.restrictDelete'), value: false }
  ]);

  readonly fkHolderEntityId = computed(() => {
    const type = this.relationshipForm.relationshipType;
    if (type === 'OneToMany') {
      return this.relationshipForm.targetEntityId || null;
    }
    if (type === 'OneToOne') {
      return this.relationshipForm.sourceEntityId || null;
    }
    return null;
  });

  readonly showForeignKeyPicker = computed(() => this.relationshipForm.relationshipType !== 'ManyToMany');

  readonly fkFieldOptions = computed<SelectOption[]>(() =>
    this.fkHolderFields().map(field => ({
      label: `${field.displayName} (${field.name})`,
      value: field.name
    }))
  );

  readonly fkConventionHint = computed(() => {
    const type = this.relationshipForm.relationshipType;
    if (type === 'OneToMany') {
      return this.t('dynamic.relationship.fkConvention.oneToMany');
    }
    if (type === 'OneToOne') {
      return this.t('dynamic.relationship.fkConvention.oneToOne');
    }
    return this.t('dynamic.relationship.fkConvention.hint');
  });

  constructor() {
    effect(() => {
      const id = this.entityId();
      if (id) {
        this.loadData(id);
      }
    });
  }

  loadData(entityId: string): void {
    this.loading.set(true);
    this.error.set(null);

    forkJoin({
      relationships: this.api.listRelationships(entityId),
      entities: this.api.listEntities()
    }).pipe(
      finalize(() => this.loading.set(false))
    ).subscribe({
      next: ({ relationships, entities }) => {
        this.relationships.set(relationships);
        this.entities.set(entities);
      },
      error: err => {
        this.relationships.set([]);
        this.error.set(err?.message ?? this.t('common.loadFailed'));
      }
    });
  }

  entityLabel(entityId: string): string {
    const match = this.entities().find(item => item.id === entityId);
    return match ? `${match.displayName} (${match.name})` : this.t('common.unknown');
  }

  relationshipTypeLabel(type: string): string {
    const key = `dynamic.relationshipType.${type}`;
    const translated = this.t(key);
    return translated === key ? type : translated;
  }

  fkOrJoinLabel(rel: DynamicRelationship): string {
    if (rel.relationshipType === 'ManyToMany') {
      return rel.joinTableName ?? this.t('dynamic.relationship.joinTable');
    }
    return rel.foreignKeyFieldName;
  }

  openAddDialog(): void {
    this.dialogMode.set('add');
    this.editingRelationshipId = null;
    this.relationshipForm = {
      ...this.emptyRelationshipForm(),
      sourceEntityId: this.entity().id
    };
    this.relationshipDialogVisible = true;
    this.loadFkFieldsIfNeeded();
  }

  openEditDialog(rel: DynamicRelationship): void {
    this.dialogMode.set('edit');
    this.editingRelationshipId = rel.id;
    this.relationshipForm = {
      sourceEntityId: rel.sourceEntityId,
      targetEntityId: rel.targetEntityId,
      relationshipType: rel.relationshipType as DynamicRelationshipType,
      foreignKeyFieldName: rel.foreignKeyFieldName,
      displayName: rel.displayName,
      isRequired: rel.isRequired,
      cascadeDelete: rel.cascadeDelete
    };
    this.relationshipDialogVisible = true;
  }

  onRelationshipEndpointsChange(): void {
    this.relationshipForm.foreignKeyFieldName = '';
    this.loadFkFieldsIfNeeded();
  }

  onRelationshipTypeChange(type: DynamicRelationshipType): void {
    this.relationshipForm.relationshipType = type;
    this.relationshipForm.foreignKeyFieldName = '';
    this.loadFkFieldsIfNeeded();
  }

  saveRelationship(): void {
    if (this.dialogMode() === 'add') {
      this.createRelationship();
      return;
    }
    this.updateRelationship();
  }

  confirmDelete(rel: DynamicRelationship): void {
    this.confirmation.confirm({
      header: this.t('dynamic.confirm.title'),
      message: this.t('dynamic.relationship.confirm.delete'),
      accept: () => this.deleteRelationship(rel.id)
    });
  }

  canViewRelationships(): boolean {
    return this.hasRelationshipAction('View');
  }

  canCreateRelationship(): boolean {
    return this.hasRelationshipAction('Create');
  }

  canUpdateRelationship(): boolean {
    return this.hasRelationshipAction('Update');
  }

  canDeleteRelationship(): boolean {
    return this.hasRelationshipAction('Delete');
  }

  resetRelationshipForm(): void {
    this.relationshipForm = this.emptyRelationshipForm();
    this.editingRelationshipId = null;
    this.fkHolderFields.set([]);
  }

  private createRelationship(): void {
    const sourceEntityId = this.relationshipForm.sourceEntityId;
    const targetEntityId = this.relationshipForm.targetEntityId;
    if (!sourceEntityId || !targetEntityId) {
      return;
    }

    const isManyToMany = this.relationshipForm.relationshipType === 'ManyToMany';
    const foreignKeyFieldName = isManyToMany
      ? '_m2m'
      : this.relationshipForm.foreignKeyFieldName.trim();

    if (!isManyToMany && !foreignKeyFieldName) {
      this.messages.add({ severity: 'warn', summary: this.t('dynamic.relationship.foreignKey') });
      return;
    }

    const request: AddRelationshipRequest = {
      sourceEntityId,
      targetEntityId,
      relationshipType: this.relationshipForm.relationshipType,
      foreignKeyFieldName,
      displayName: this.relationshipForm.displayName.trim() || undefined,
      isRequired: this.relationshipForm.isRequired,
      cascadeDelete: this.relationshipForm.cascadeDelete
    };

    this.savingRelationship.set(true);
    this.api.addRelationship(this.entity().id, request).pipe(
      finalize(() => this.savingRelationship.set(false))
    ).subscribe({
      next: created => {
        this.relationshipDialogVisible = false;
        this.messages.add({ severity: 'success', summary: this.t('dynamic.relationship.saved') });
        if (created.pendingMigrationId) {
          this.pendingMigrationNotice.set(created.pendingMigrationId);
          this.messages.add({
            severity: 'info',
            summary: this.t('dynamic.relationship.pendingMigrationMessage'),
            life: 8000
          });
        }
        this.loadData(this.entity().id);
        this.refresh.emit();
      },
      error: () => this.messages.add({ severity: 'error', summary: this.t('common.saveFailed') })
    });
  }

  private updateRelationship(): void {
    const relationshipId = this.editingRelationshipId;
    if (!relationshipId) {
      return;
    }

    this.savingRelationship.set(true);
    this.api.updateRelationship(relationshipId, {
      isRequired: this.relationshipForm.isRequired,
      cascadeDelete: this.relationshipForm.cascadeDelete
    }).pipe(
      finalize(() => this.savingRelationship.set(false))
    ).subscribe({
      next: () => {
        this.relationshipDialogVisible = false;
        this.messages.add({ severity: 'success', summary: this.t('dynamic.relationship.saved') });
        this.loadData(this.entity().id);
        this.refresh.emit();
      },
      error: () => this.messages.add({ severity: 'error', summary: this.t('common.saveFailed') })
    });
  }

  private deleteRelationship(relationshipId: string): void {
    this.api.deleteRelationship(relationshipId).subscribe({
      next: () => {
        this.messages.add({ severity: 'success', summary: this.t('dynamic.relationship.deleted') });
        this.loadData(this.entity().id);
        this.refresh.emit();
      },
      error: () => this.messages.add({ severity: 'error', summary: this.t('common.saveFailed') })
    });
  }

  private loadFkFieldsIfNeeded(): void {
    const fkHolderId = this.fkHolderEntityId();
    if (fkHolderId && this.showForeignKeyPicker()) {
      this.loadFkHolderFields(fkHolderId);
      return;
    }
    this.fkHolderFields.set([]);
  }

  private loadFkHolderFields(entityId: string): void {
    this.api.getEntity(entityId).subscribe({
      next: detail => this.fkHolderFields.set(detail.fields ?? []),
      error: () => this.fkHolderFields.set([])
    });
  }

  private emptyRelationshipForm(): RelationshipFormState {
    return {
      sourceEntityId: '',
      targetEntityId: '',
      relationshipType: 'OneToMany',
      foreignKeyFieldName: '',
      displayName: '',
      isRequired: false,
      cascadeDelete: false
    };
  }

  private hasRelationshipAction(action: string): boolean {
    const context = this.contextService.context();
    const roles = new Set(context?.roles ?? []);
    if (roles.has('SystemAdmin')) {
      return true;
    }

    const node = (context?.permissions ?? []).find(item => item.key === 'dynamic.relationships');
    if (!node) {
      return false;
    }

    const actions = new Set((node.actions ?? []).map(item => item.toLowerCase()));
    return actions.has(action.toLowerCase());
  }

  t(key: string): string {
    return this.localization.translate(key);
  }
}
